import { type TreeNode } from '@/components/Treeview';
import { DownloadMedicalCategory } from '@/feature/downloads/types';
import {
  DOWNLOAD_CATEGORY_LABELS,
  DOWNLOAD_CATEGORY_ORDER,
  DOWNLOAD_MEDICAL_CATEGORY_VALUES,
} from '../constants';

export type DocumentNodeData = { count?: number };
export type DocumentNode = TreeNode<DocumentNodeData>;

type DownloadDocumentsData = {
  medical?: {
    count: number;
    categories: {
      name: DownloadMedicalCategory;
      count: number;
      categories?: { name: string; count: number }[];
    }[];
  };
  nonMedical?: { count: number };
};

/**
 * Maps the backend available-parameters response into TreeView-compatible nodes.
 * Categories are displayed in the fixed `DOWNLOAD_CATEGORY_ORDER` sequence.
 * Categories absent from the response are shown as disabled with count 0.
 */
export const mapDocumentsToNodes = (
  data: DownloadDocumentsData | undefined,
): DocumentNode[] => [
  {
    id: 'medical-records',
    label: 'Medical Records',
    count: data?.medical?.count,
    disabled: data?.medical?.count === 0,
    childNodes: DOWNLOAD_CATEGORY_ORDER.map((categoryName) => {
      const item = data?.medical?.categories?.find(
        (c) => c.name === categoryName,
      );
      return {
        id: categoryName,
        label: DOWNLOAD_CATEGORY_LABELS[categoryName],
        count: item?.count,
        disabled: !item || item.count === 0,
        childNodes: item?.categories?.map((sub) => ({
          id: `${categoryName}::${sub.name}`,
          label: sub.name,
          count: sub.count,
        })),
      };
    }),
  },
  {
    id: 'non-medical-documents',
    label: 'Non-medical Documents',
    count: data?.nonMedical?.count,
    disabled: data?.nonMedical?.count === 0,
  },
];

/**
 * Maps TreeView selected leaf IDs back into the submit-download API shape.
 * Bare category IDs (leaves without subcategories) become `{ category, subcategories: undefined }`.
 * Compound `category::sub` IDs are grouped by category.
 */
export const mapSelectedIdsToDownloadPayload = (selectedIds: string[]) => {
  const nonMedical = selectedIds.includes('non-medical-documents');

  const medicalMap = selectedIds.reduce<Record<string, string[]>>((acc, id) => {
    const sepIdx = id.indexOf('::');
    if (sepIdx !== -1) {
      const cat = id.slice(0, sepIdx);
      (acc[cat] ??= []).push(id.slice(sepIdx + 2));
    } else if (
      DOWNLOAD_MEDICAL_CATEGORY_VALUES.has(id as DownloadMedicalCategory)
    ) {
      acc[id] ??= [];
    }
    return acc;
  }, {});

  const categories = Object.keys(medicalMap);
  const medical =
    categories.length > 0
      ? categories.map((category) => ({
          category: category as DownloadMedicalCategory,
          subcategories:
            medicalMap[category].length > 0 ? medicalMap[category] : undefined,
        }))
      : undefined;

  return { nonMedical, medical };
};
